// src/context/PredictorAuthContext.tsx
"use client";

import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { 
  getCurrentUser, 
  logoutPredictor,
  checkAuth, 
  PredictorLoginResponse
} from '@/utils/predictorAuthApi';

interface PredictorUser {
  id: string;
  username: string;
  fullName: string;
  email: string;
  cohort: 'TO' | 'GP';
  groupNumber: number;
  credentialType: 'before' | 'after';
  loginTime?: string;
  lastActivity?: string;
}

interface PredictorAuthContextType {
  isAuthenticated: boolean;
  user: PredictorUser | null;
  loading: boolean;
  checkAuthStatus: () => Promise<boolean>;
  logoutUser: () => Promise<void>;
  login: (loginResponse: PredictorLoginResponse) => void;
}

const PredictorAuthContext = createContext<PredictorAuthContextType | undefined>(undefined);

interface PredictorAuthProviderProps {
  children: ReactNode;
}

export function PredictorAuthProvider({ children }: PredictorAuthProviderProps) {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [user, setUser] = useState<PredictorUser | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  const predictorCheckAuth = async (): Promise<boolean> => {
    try {
      setLoading(true);
      
      // Check if we have user info in localStorage
      const storedUserInfo = localStorage.getItem('predictor_user_info');
      let userData = null;
      
      if (storedUserInfo) {
        userData = JSON.parse(storedUserInfo);
      }
      
      // Verify with the server using checkAuth function from API
      const response = await checkAuth();
      
      if (response.isAuthenticated) {
        setIsAuthenticated(true);
        
        // Get full user data from /me endpoint
        try {
          const fullUserResponse = await getCurrentUser();
          if (fullUserResponse && fullUserResponse.success && fullUserResponse.user) {
            setUser(fullUserResponse.user);
            
            // Update localStorage with latest user data
            localStorage.setItem('predictor_user_info', JSON.stringify(fullUserResponse.user));
          } else {
            // Fall back to basic user data from status endpoint or localStorage
            setUser(response.user || userData);
            if (response.user) {
              localStorage.setItem('predictor_user_info', JSON.stringify(response.user));
            }
          }
        } catch (error) {
          console.warn('Failed to get full user data, using basic data:', error);
          // Fall back to basic user data from status endpoint or localStorage
          setUser(response.user || userData);
          if (response.user) {
            localStorage.setItem('predictor_user_info', JSON.stringify(response.user));
          }
        }
        
        return true;
      } else {
        // Clear auth state if server says not authenticated
        setIsAuthenticated(false);
        setUser(null);
        localStorage.removeItem('predictor_user_info');
        return false;
      }
    } catch (error) {
      console.error('Error checking predictor authentication status:', error);
      setIsAuthenticated(false);
      setUser(null);
      localStorage.removeItem('predictor_user_info');
      return false;
    } finally {
      setLoading(false);
    }
  };

  const logoutUser = async (): Promise<void> => {
    try {
      // Call logout API
      await logoutPredictor();
      
      // Clear auth state
      setIsAuthenticated(false);
      setUser(null);
      localStorage.removeItem('predictor_user_info');
    } catch (error) {
      console.error('Error during predictor logout:', error);
      // Still clear local state even if API fails
      setIsAuthenticated(false);
      setUser(null);
      localStorage.removeItem('predictor_user_info');
    }
  };

  const login = (loginResponse: PredictorLoginResponse): void => {
    try {
      setLoading(true);
      
      if (loginResponse.success && loginResponse.user) {
        setIsAuthenticated(true);
        setUser(loginResponse.user);
        
        // Store user data in localStorage
        localStorage.setItem('predictor_user_info', JSON.stringify(loginResponse.user));
      } else {
        throw new Error(loginResponse.message || 'Login failed');
      }
    } catch (error) {
      console.error('Login error:', error);
      throw error; // Re-throw so the component can handle it
    } finally {
      setLoading(false);
    }
  };

  // Check auth on initial load - SAME PATTERN AS ADMIN
  useEffect(() => {
    predictorCheckAuth();
    
    // Set up a timer to check auth periodically (every 10 minutes)
    // Note: Predictors might not have refresh tokens, so we just re-check auth
    const refreshInterval = setInterval(() => {
      if (isAuthenticated) {
        predictorCheckAuth()
          .then(isStillAuthenticated => {
            if (!isStillAuthenticated) {
              // If auth check fails, user is no longer authenticated
              console.log('Predictor session expired, clearing auth state');
            }
          })
          .catch(() => {
            // If auth check throws error, re-check once more
            predictorCheckAuth();
          });
      }
    }, 10 * 60 * 1000); // 10 minutes
    
    return () => clearInterval(refreshInterval);
  }, [isAuthenticated]);

  return (
    <PredictorAuthContext.Provider value={{ 
      isAuthenticated, 
      user, 
      loading, 
      checkAuthStatus: predictorCheckAuth, // Export internal function as checkAuthStatus
      logoutUser,
      login
    }}>
      {children}
    </PredictorAuthContext.Provider>
  );
}

export const usePredictorAuth = () => {
  const context = useContext(PredictorAuthContext);
  if (context === undefined) {
    throw new Error('usePredictorAuth must be used within a PredictorAuthProvider');
  }
  return context;
};